import { useState, useEffect, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { ArrowLeftIcon, ArrowRightIcon, ArrowDownTrayIcon, PrinterIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';

// Import react-pdf styles
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
// Use jsdelivr CDN which has good version coverage
// The version from pdfjs.version should match the installed package
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function PDFViewer({ url, title, isOpen, onClose }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const pdfScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);

  const stopScrollAnimation = () => {
    if (scrollAnimationRef.current !== null) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    console.error('PDF URL:', url);
    // Check if it's a CORS error
    const errorMessage = error.message || 'Failed to load PDF';
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      setError('PDF cannot be loaded due to CORS restrictions. Please download it instead.');
    } else {
      setError(`Failed to load PDF: ${errorMessage}. Please try downloading it instead.`);
    }
  };

  // Reset when modal opens or URL changes
  useEffect(() => {
    stopScrollAnimation();
    if (isOpen) {
      setError(null);
      setCurrentPage(1);
      setNumPages(0);
      setScale(1.0);
    }
  }, [isOpen, url]);

  useEffect(() => () => stopScrollAnimation(), []);

  // Scroll thumbnail into view when current page changes (only in sidebar)
  useEffect(() => {
    if (sidebarRef.current && currentPage > 0) {
      const thumbnailElement = document.getElementById(`thumbnail-${currentPage}`);
      if (thumbnailElement && sidebarRef.current.contains(thumbnailElement)) {
        // Only scroll within the sidebar container
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const elementRect = thumbnailElement.getBoundingClientRect();
        const scrollTop = sidebarRef.current.scrollTop;
        const elementOffsetTop = elementRect.top - sidebarRect.top + scrollTop;
        
        sidebarRef.current.scrollTo({
          top: elementOffsetTop - 20, // 20px offset from top
          behavior: 'smooth'
        });
      }
    }
  }, [currentPage]);

  const scrollToPage = (page: number) => {
    const pageElement = document.getElementById(`pdf-page-${page}`);
    const container = pdfScrollContainerRef.current;
    if (!pageElement || !container) return;

    const pageRect = pageElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const top = Math.max(0, pageRect.top - containerRect.top + container.scrollTop);

    setCurrentPage(page);
    stopScrollAnimation();

    const start = container.scrollTop;
    const distance = top - start;
    if (Math.abs(distance) < 1) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      container.scrollTop = top;
      return;
    }

    const duration = 300;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);
      container.scrollTop = start + distance * easeOutCubic(progress);
      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimationRef.current = null;
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(step);
  };

  const goToPrevPage = () => {
    const prevPage = Math.max(1, currentPage - 1);
    scrollToPage(prevPage);
  };

  const goToNextPage = () => {
    const nextPage = Math.min(numPages, currentPage + 1);
    scrollToPage(nextPage);
  };

  // Track which page is currently in view
  useEffect(() => {
    if (numPages === 0) return;

    const container = pdfScrollContainerRef.current;
    if (!container) return;

    const observerOptions = {
      root: container, // Use the scroll container as root
      rootMargin: '-10% 0px -80% 0px', // Page is considered "in view" when it's in the top 10% of the container
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      const topEntry = visible[0];
      if (!topEntry) return;

      const targetElement = topEntry.target as HTMLElement;
      if (targetElement?.id) {
        const pageNum = parseInt(targetElement.id.replace('pdf-page-', ''), 10);
        if (!isNaN(pageNum)) {
          setCurrentPage(pageNum);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all page elements
    for (let i = 1; i <= numPages; i++) {
      const pageElement = document.getElementById(`pdf-page-${i}`);
      if (pageElement) {
        observer.observe(pageElement);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [numPages]);

  const handleDownload = () => {
    window.open(url, '_blank');
  };

  const handlePrint = () => {
    const printWindow = window.open(url, '_blank');
    printWindow?.addEventListener('load', () => {
      printWindow.print();
    });
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(2.0, prev + 0.25));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  };

  // Memoize PDF.js options to prevent unnecessary reloads
  const pdfOptions = useMemo(
    () => ({
      cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/standard_fonts/`,
    }),
    [] // Empty deps since pdfjs.version shouldn't change
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" title={title || 'PDF Viewer'}>
      <div className="flex flex-col flex-1 min-h-0 h-[80vh]">
        {/* Controls */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Previous page"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 px-2 min-w-[100px] text-center">
              Page {currentPage} of {numPages}
            </span>
            <button
              onClick={goToNextPage}
              disabled={currentPage >= numPages}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Next page"
            >
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Zoom out"
            >
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 px-3 min-w-[70px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={scale >= 2.0}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Zoom in"
            >
              <MagnifyingGlassPlusIcon className="h-5 w-5" />
            </button>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <button
              onClick={handleDownload}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Download PDF"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            <button
              onClick={handlePrint}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Print PDF"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {numPages > 0 && !error && (
            <div
              ref={sidebarRef}
              className="border-r border-gray-200 bg-gray-50 overflow-y-auto overscroll-contain flex-shrink-0 min-h-0 w-[140px]"
            >
              <div ref={thumbnailContainerRef} className="p-3 space-y-3 flex flex-col items-center">
                <Document file={url} options={pdfOptions}>
                  {Array.from({ length: numPages }, (_, index) => index + 1).map((pageNum) => (
                    <button
                      key={`thumbnail-${pageNum}`}
                      id={`thumbnail-${pageNum}`}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        scrollToPage(pageNum);
                      }}
                      className={`w-full flex flex-col items-center p-2 rounded-md border-2 transition-all ${
                        currentPage === pageNum
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                      aria-label={`Go to page ${pageNum}`}
                    >
                      <div className="relative flex justify-center w-full">
                        <Page
                          pageNumber={pageNum}
                          width={96}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="pointer-events-none"
                          loading={
                            <div className="flex items-center justify-center h-24 bg-gray-100 rounded">
                              <Spinner size={24} />
                            </div>
                          }
                        />
                        {currentPage === pageNum && (
                          <div className="absolute inset-0 border-2 border-primary rounded pointer-events-none" />
                        )}
                      </div>
                      <div className="mt-2 text-xs text-center font-medium text-gray-600">
                        {pageNum}
                      </div>
                    </button>
                  ))}
                </Document>
              </div>
            </div>
          )}

          {/* PDF Content */}
          <div
            ref={pdfScrollContainerRef}
            className="flex-1 min-h-0 overflow-auto overscroll-contain bg-gray-100 p-4 pdf-scroll-container"
          >
          {error && (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <p className="text-error mb-4">Error loading PDF: {error}</p>
                <Button onClick={handleDownload} variant="primary">
                  Download PDF
                </Button>
              </div>
            </div>
          )}

          {!error && (
            <div className="flex flex-col items-center gap-4">
              <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                      <Spinner size={80} className="mx-auto mb-4" />
                      <p className="text-gray-600">Loading PDF...</p>
                    </div>
                  </div>
                }
                options={pdfOptions}
              >
                {numPages > 0 && (
                  <>
                    {Array.from({ length: numPages }, (_, index) => index + 1).map((pageNum) => (
                      <div
                        key={`page-wrapper-${pageNum}`}
                        id={`pdf-page-${pageNum}`}
                        className="mb-4 flex justify-center"
                      >
                        <Page
                          key={`page-${pageNum}-${scale}`}
                          pageNumber={pageNum}
                          scale={scale}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                          className="shadow-lg"
                          loading={
                            <div className="flex items-center justify-center min-h-[400px]">
                              <Spinner size={60} />
                            </div>
                          }
                          onRenderError={(error) => {
                            // Silently handle render errors (like transport destroyed)
                            // These are often non-critical and happen during page/zoom changes
                            console.warn(`Page ${pageNum} render error (non-critical):`, error);
                          }}
                        />
                      </div>
                    ))}
                  </>
                )}
              </Document>
            </div>
          )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

